import { useEffect, useState } from "react";
import dayjs from "dayjs";
import { api, apiError } from "../lib/api";
import { PageHeader, Card, Btn, Empty, Table, Td } from "../components/UI";
import { FLabel, FSelect, FTextarea } from "./engagement/FormControls";
import { DateField } from "./engagement/DateField";
import { SlidePanel, ConfirmDialog } from "../components/SlidePanel";
import { useClients, ClientSelect } from "./clinical/shared";
import { usePagination, PaginationBar } from "../components/Pagination";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";

const RATING_LABELS = ["0 - None", "1 - Mild", "2 - Moderate", "3 - Severe", "4 - Very Severe"];
const EMPTY_DIMS = Array.from({ length: 6 }, () => ({ rating: 0, comments: "" }));

function suggestLoc(dims) {
  const ratings = dims.map((d) => Number(d.rating) || 0);
  const mx = Math.max(...ratings), avg = ratings.reduce((a, b) => a + b, 0) / ratings.length;
  if (mx >= 4) return "4.0";
  if (mx === 3 && avg >= 2) return "3.5";
  if (mx === 3) return "3.1";
  if (avg >= 1.5) return "2.5";
  if (mx === 2) return "2.1";
  if (mx === 1) return "1.0";
  return "0.5";
}

function AsamForm({ open, onClose, config, clients, editRow, onSaved }) {
  const [clientId, setClientId] = useState("");
  const [date, setDate] = useState(dayjs().format("YYYY-MM-DD"));
  const [dims, setDims] = useState(EMPTY_DIMS);
  const [recLoc, setRecLoc] = useState("");
  const [rationale, setRationale] = useState("");
  const [status, setStatus] = useState("draft");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (editRow) {
      setClientId(editRow.client_id); setDate(editRow.assessment_date);
      setDims(editRow.dimensions.map((d) => ({ rating: d.rating, comments: d.comments })));
      setRecLoc(editRow.recommended_loc); setRationale(editRow.rationale); setStatus(editRow.status);
    } else {
      setClientId(""); setDate(dayjs().format("YYYY-MM-DD")); setDims(EMPTY_DIMS.map((d) => ({ ...d })));
      setRecLoc(""); setRationale(""); setStatus("draft");
    }
  }, [open, editRow]);

  const suggested = suggestLoc(dims);
  const setDim = (i, patch) => setDims((s) => s.map((d, j) => (j === i ? { ...d, ...patch } : d)));

  const save = async () => {
    if (!clientId) return toast.error("Select a client");
    setBusy(true);
    try {
      const body = { client_id: clientId, assessment_date: date, dimensions: dims, recommended_loc: recLoc || suggested, rationale, status };
      if (editRow) await api.put(`/asam/${editRow.id}`, body);
      else await api.post("/asam", body);
      toast.success(editRow ? "ASAM assessment updated" : "ASAM assessment saved");
      onSaved();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <SlidePanel open={open} onClose={onClose} title={editRow ? "Edit ASAM Assessment" : "New ASAM Assessment"}
      subtitle="ASAM Criteria - six-dimension multidimensional assessment" width="max-w-3xl" testId="asam-form-panel">
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div><FLabel>Client *</FLabel><ClientSelect clients={clients} value={clientId} onChange={setClientId} testId="asam-client-select" disabled={!!editRow} /></div>
          <div><FLabel>Assessment date</FLabel><DateField testId="asam-date" value={date} onChange={setDate} /></div>
        </div>
        {config.dimensions.map((dim, i) => (
          <div key={dim.num} className="rounded-xl border border-[#EFEBE0] p-4" data-testid={`asam-dim-${dim.num}`}>
            <div className="mb-1 text-sm font-bold text-[#3E2723]">Dimension {dim.num}: {dim.title}</div>
            <div className="mb-2 text-xs text-[#84675B]">{dim.desc}</div>
            <div className="mb-2 flex flex-wrap gap-2">
              {RATING_LABELS.map((label, r) => (
                <button key={r} type="button" data-testid={`asam-dim${dim.num}-rating${r}`}
                  onClick={() => setDim(i, { rating: r })}
                  className={`rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${dims[i].rating === r ? "bg-[#4E342E] text-white" : "bg-[#F5F2EB] text-[#6D4C41] hover:bg-[#EDE7DC]"}`}>
                  {label}
                </button>
              ))}
            </div>
            <FTextarea data-testid={`asam-dim${dim.num}-comments`} rows={2} placeholder="Clinical comments for this dimension…"
              value={dims[i].comments} onChange={(e) => setDim(i, { comments: e.target.value })} />
          </div>
        ))}
        <div className="rounded-2xl bg-[#F5F2EB] p-4" data-testid="asam-loc-section">
          <div className="mb-2 text-sm font-bold text-[#3E2723]">Level of Care</div>
          <div className="mb-3 text-sm text-[#5D4037]">System-suggested: <span className="font-bold" data-testid="asam-suggested-loc">{suggested} - {(config.levels.find((l) => l.code === suggested) || {}).label}</span></div>
          <div className="grid grid-cols-2 gap-4">
            <div><FLabel>Recommended level (clinician)</FLabel>
              <FSelect data-testid="asam-recommended-loc" value={recLoc} onChange={(e) => setRecLoc(e.target.value)} placeholder={`Use suggested (${suggested})`}>
                {config.levels.map((l) => <option key={l.code} value={l.code}>{l.code} - {l.label}</option>)}
              </FSelect></div>
            <div><FLabel>Status</FLabel>
              <FSelect data-testid="asam-status" value={status} onChange={(e) => setStatus(e.target.value)} placeholderDisabled>
                <option value="draft">Draft</option><option value="completed">Completed</option>
              </FSelect></div>
          </div>
          <div className="mt-3"><FLabel>Rationale / clinical justification</FLabel>
            <FTextarea data-testid="asam-rationale" rows={3} value={rationale} onChange={(e) => setRationale(e.target.value)} /></div>
        </div>
        <div className="flex justify-end gap-2">
          <Btn variant="secondary" onClick={onClose} data-testid="asam-cancel-btn">Cancel</Btn>
          <Btn onClick={save} disabled={busy} data-testid="asam-save-btn">{busy ? "Saving…" : "Save Assessment"}</Btn>
        </div>
      </div>
    </SlidePanel>
  );
}

export default function Asam() {
  const [rows, setRows] = useState([]);
  const [config, setConfig] = useState(null);
  const [filterClient, setFilterClient] = useState("");
  const [formOpen, setFormOpen] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [delRow, setDelRow] = useState(null);
  const clients = useClients();
  const pg = usePagination(rows);

  const load = () => { api.get(`/asam${filterClient ? `?client_id=${filterClient}` : ""}`).then((r) => setRows(r.data)).catch((e) => toast.error(apiError(e))); };
  useEffect(() => { api.get("/asam/config").then((r) => setConfig(r.data)).catch(() => {}); }, []);
  useEffect(load, [filterClient]);

  const remove = async () => {
    try { await api.delete(`/asam/${delRow.id}`); toast.success("Deleted"); setDelRow(null); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const locLabel = (code) => { const l = config?.levels.find((x) => x.code === code); return l ? `${l.code} - ${l.label}` : code; };

  return (
    <div data-testid="asam-page">
      <PageHeader title="ASAM Criteria" subtitle="Six-dimension assessments with level-of-care recommendations"
        action={<Btn onClick={() => { setEditRow(null); setFormOpen(true); }} data-testid="asam-new-btn"><Plus size={15} /> New ASAM Assessment</Btn>} />
      <div className="mb-4 w-64"><FLabel>Filter by client</FLabel><ClientSelect clients={clients} value={filterClient} onChange={setFilterClient} testId="asam-filter-client" /></div>
      <Card className="overflow-hidden" data-testid="asam-table">
        {rows.length === 0 ? <Empty text="No ASAM assessments yet." /> : (
          <Table headers={["Date", "Client", "Dimension Ratings", "Recommended LOC", "Status", "Assessor", ""]}>
            {pg.pageRows.map((r) => (
              <tr key={r.id} className="border-t border-[#F1EEE7] hover:bg-[#FDFCF8]" data-testid={`asam-row-${r.id}`}>
                <Td>{dayjs(r.assessment_date).format("MM/DD/YYYY")}</Td>
                <Td className="font-medium">{r.client_name}</Td>
                <Td>
                  <div className="flex gap-1">
                    {r.dimensions.map((d) => (
                      <span key={d.num} title={`Dimension ${d.num}`}
                        className={`flex h-6 w-6 items-center justify-center rounded-md text-[11px] font-bold ${d.rating >= 3 ? "bg-[#FFEBEE] text-[#C62828]" : d.rating === 2 ? "bg-[#FFF3E0] text-[#B84A00]" : "bg-[#F5F2EB] text-[#6D4C41]"}`}>
                        {d.rating}
                      </span>
                    ))}
                  </div>
                </Td>
                <Td className="text-xs">{locLabel(r.recommended_loc)}</Td>
                <Td><span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${r.status === "completed" ? "bg-[#E8F5E9] text-[#2E7D32]" : "bg-[#FFF8E1] text-[#8D6E00]"}`}>{r.status === "completed" ? "Completed" : "Draft"}</span></Td>
                <Td className="text-[#7B6157]">{r.assessor}</Td>
                <Td>
                  <div className="flex gap-1">
                    <button data-testid={`asam-edit-${r.id}`} onClick={() => { setEditRow(r); setFormOpen(true); }} className="rounded-lg p-1.5 text-[#6D4C41] hover:bg-[#F5F2EB]"><Pencil size={15} /></button>
                    <button data-testid={`asam-delete-${r.id}`} onClick={() => setDelRow(r)} className="rounded-lg p-1.5 text-[#C62828] hover:bg-[#FFEBEE]"><Trash2 size={15} /></button>
                  </div>
                </Td>
              </tr>
            ))}
          </Table>
        )}
        <PaginationBar {...pg} testId="asam-pagination" />
      </Card>
      {config && <AsamForm open={formOpen} onClose={() => setFormOpen(false)} config={config} clients={clients}
        editRow={editRow} onSaved={() => { setFormOpen(false); load(); }} />}
      <ConfirmDialog open={!!delRow} onClose={() => setDelRow(null)} onConfirm={remove}
        description={`Delete this ASAM assessment for ${delRow?.client_name}?`} />
    </div>
  );
}
